import { type PropsWithChildren, forwardRef } from "react";

import type { ModalProps } from "./Modal.types";
import { cn } from "$/lib/utils/functions/misc.functions";
import CloseIcon from "$/lib/icons/CloseIcon";
import Flexbox from "../../common/Flexbox";
import { createPortal } from "react-dom";

const Modal = forwardRef<HTMLDivElement, PropsWithChildren<ModalProps>>(
  (
    { isOpen, onClose, title, children, className, titleClassName },
    forwardedRef,
  ) => {
    if (!isOpen) return null;

    return createPortal(
      <>
        {isOpen && (
          <div
            className="fixed inset-0 z-[1000] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm"
            onClick={onClose}
          >
            <div
              ref={forwardedRef}
              className={cn(
                "min-w-md max-h-[90vh] w-fit transform overflow-auto rounded-[20px] border border-neutral-500 bg-white p-6 text-black shadow-lg transition-all duration-300",
                { "animate-popup": isOpen },
                className,
              )}
              onClick={(e) => e.stopPropagation()}
            >
              {(title || onClose) && (
                <Flexbox row justify="between" align="center" className="pb-6">
                  <p
                    className={cn(
                      "text-base font-semibold text-secondary",
                      titleClassName,
                    )}
                  >
                    {title}
                  </p>

                  {onClose && (
                    <button
                      type="button"
                      className="flex items-center"
                      onClick={onClose}
                    >
                      <CloseIcon className="size-4" color="black" />
                    </button>
                  )}
                </Flexbox>
              )}
              {children}
            </div>
          </div>
        )}
      </>,
      document.body,
    );
  },
);

Modal.displayName = "Modal";

export default Modal;
